import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Tag, Plus, Edit, Trash2, Users, CloudDownload, Download, RefreshCw, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

import { API_URL } from '@/config/api';

const colorOptions = [
  { name: 'Red', value: '#EF4444' },
  { name: 'Orange', value: '#F97316' },
  { name: 'Amber', value: '#F59E0B' },
  { name: 'Yellow', value: '#EAB308' },
  { name: 'Lime', value: '#84CC16' },
  { name: 'Green', value: '#22C55E' },
  { name: 'Emerald', value: '#10B981' },
  { name: 'Teal', value: '#14B8A6' },
  { name: 'Cyan', value: '#06B6D4' },
  { name: 'Sky', value: '#0EA5E9' },
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Indigo', value: '#6366F1' },
  { name: 'Violet', value: '#8B5CF6' },
  { name: 'Purple', value: '#A855F7' },
  { name: 'Pink', value: '#EC4899' },
  { name: 'Rose', value: '#F43F5E' },
];

const TagsManager = () => {
  const { user, selectedBusiness } = useAuth();
  const [tags, setTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [formData, setFormData] = useState({ name: '', color: '#3B82F6', description: '' });
  const [remoteTags, setRemoteTags] = useState([]);
  const [selectedImports, setSelectedImports] = useState({});
  const [importing, setImporting] = useState(false);
  const [fetchingRemote, setFetchingRemote] = useState(false);
  const [ghlConnected, setGhlConnected] = useState(false);

  useEffect(() => {
    fetchTags();
    checkGhlConnection();
  }, [selectedBusiness]);

  const checkGhlConnection = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/ghl/credentials`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setGhlConnected(data?.is_connected || false);
      }
    } catch { setGhlConnected(false); }
  };

  const fetchTags = async () => {
    try {
      const token = localStorage.getItem('token');
      const businessParam = selectedBusiness ? `?business_id=${selectedBusiness.business_id}` : '';
      const response = await fetch(`${API_URL}/api/tags${businessParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) { setTags(await response.json()); }
    } catch (error) { console.error('Error fetching tags:', error); }
    finally { setIsLoading(false); }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) { toast.error('Tag name is required'); return; }
    if (!selectedBusiness && user?.role !== 'business_admin') {
      toast.error('Please select a business first'); return;
    }
    try {
      const token = localStorage.getItem('token');
      const businessParam = selectedBusiness ? `?business_id=${selectedBusiness.business_id}` : '';
      const url = editingTag 
        ? `${API_URL}/api/tags/${editingTag.tag_id}`
        : `${API_URL}/api/tags${businessParam}`;
      const response = await fetch(url, {
        method: editingTag ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        toast.success(editingTag ? 'Tag updated!' : 'Tag created!');
        setIsModalOpen(false);
        setEditingTag(null);
        setFormData({ name: '', color: '#3B82F6', description: '' });
        fetchTags();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save tag');
      }
    } catch (error) { toast.error('Failed to save tag'); }
  };

  const handleDelete = async (tagId) => {
    if (!confirm('Are you sure? This will remove the tag from all leads.')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tags/${tagId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) { toast.success('Tag deleted'); fetchTags(); }
    } catch (error) { toast.error('Failed to delete tag'); }
  };

  const openEditModal = (tag) => {
    setEditingTag(tag);
    setFormData({ name: tag.name, color: tag.color, description: tag.description || '' });
    setIsModalOpen(true);
  };

  const openCreateModal = () => {
    setEditingTag(null);
    setFormData({ name: '', color: '#3B82F6', description: '' });
    setIsModalOpen(true);
  };

  const fetchRemoteTags = async () => {
    setFetchingRemote(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/ghl/fetch-remote-tags`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const tags = data.tags || [];
        setRemoteTags(tags);
        const initSelected = {};
        tags.forEach(t => { initSelected[t.id || t.name] = false; });
        setSelectedImports(initSelected);
        setShowImportModal(true);
        toast.success(`Found ${tags.length} tag(s) in GHL`);
      } else {
        toast.error('Failed to fetch tags from GHL');
      }
    } catch (error) {
      toast.error('Failed to fetch from GHL');
    } finally {
      setFetchingRemote(false);
    }
  };

  const handleImportSelectedTags = async () => {
    const toImport = remoteTags.filter(t => selectedImports[t.id || t.name] && !t.already_exists);
    if (toImport.length === 0) { toast.error('No new tags selected'); return; }
    setImporting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/ghl/import-tags`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ tags: toImport })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setShowImportModal(false);
        fetchTags();
      } else { toast.error('Failed to import tags'); }
    } catch (error) { toast.error('Failed to import tags'); }
    finally { setImporting(false); }
  };

  const toggleImportSelection = (key) => {
    setSelectedImports(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="space-y-4" data-testid="tags-manager">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between p-4 sm:p-6">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Tag className="w-5 h-5 text-cyan-500" />
            Tags
          </CardTitle>
          <div className="flex items-center gap-2">
            {ghlConnected && (
              <Button
                variant="outline"
                size="sm"
                onClick={fetchRemoteTags}
                disabled={fetchingRemote}
                data-testid="import-tags-from-ghl-btn"
              >
                <CloudDownload className={`w-4 h-4 mr-1 ${fetchingRemote ? 'animate-spin' : ''}`} />
                Import from GHL
              </Button>
            )}
            <Button 
              onClick={openCreateModal}
              size="sm"
              className="bg-gradient-to-r from-cyan-500 to-blue-600 h-9"
              data-testid="add-tag-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add Tag
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : tags.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Tag className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p>No tags created yet</p>
              <p className="text-sm">Tags help you organize and filter leads</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {tags.map((tag) => (
                <div 
                  key={tag.tag_id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200"
                  data-testid={`tag-item-${tag.tag_id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color }} />
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 truncate">{tag.name}</p>
                      <p className="text-xs text-slate-500 flex items-center gap-1">
                        <Users className="w-3 h-3" /> {tag.lead_count || 0} leads
                        {tag.ghl_tag_id && (
                          <span className="text-orange-500 ml-1">| GHL</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => openEditModal(tag)} className="h-8 w-8 p-0">
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(tag.tag_id)}
                      className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-tag-${tag.tag_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Import from GHL Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CloudDownload className="w-5 h-5 text-orange-500" />
              Import Tags from GHL
            </DialogTitle>
            <DialogDescription>Select which GHL tags to import into Beluga Pro</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[400px] py-4">
            {remoteTags.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <p>No tags found in GHL</p>
              </div>
            ) : (
              <div className="space-y-2">
                {remoteTags.map((tag) => {
                  const key = tag.id || tag.name;
                  return (
                    <div key={key}
                      className={`p-3 border rounded-lg transition-colors ${
                        tag.already_exists
                          ? 'bg-green-50 border-green-200 opacity-70'
                          : selectedImports[key]
                            ? 'bg-cyan-50 border-cyan-300'
                            : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`remote-tag-${key}`}>
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={tag.already_exists || selectedImports[key] || false}
                          onCheckedChange={() => toggleImportSelection(key)}
                          disabled={tag.already_exists}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-900">{tag.name}</p>
                            {tag.already_exists && (
                              <Badge className="text-[10px] bg-green-100 text-green-700">Already Exists</Badge>
                            )}
                          </div>
                        </div>
                        {tag.already_exists && <CheckCircle className="w-4 h-4 text-green-500" />}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportModal(false)}>Cancel</Button>
            <Button onClick={handleImportSelectedTags}
              disabled={importing || Object.values(selectedImports).every(v => !v)}
              data-testid="confirm-import-tags-btn">
              {importing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Import Selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingTag ? 'Edit Tag' : 'Create Tag'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Tag Name</Label>
              <Input placeholder="e.g., Hot Lead, VIP, Paid" className="mt-1.5"
                value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
            </div>
            <div>
              <Label>Color</Label>
              <div className="grid grid-cols-8 gap-2 mt-1.5">
                {colorOptions.map((color) => (
                  <button key={color.value} type="button"
                    onClick={() => setFormData({ ...formData, color: color.value })}
                    className={`w-8 h-8 rounded-full transition-all ${
                      formData.color === color.value ? 'ring-2 ring-offset-2 ring-slate-400 scale-110' : 'hover:scale-110'
                    }`}
                    style={{ backgroundColor: color.value }} title={color.name} />
                ))}
              </div>
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input placeholder="What is this tag used for?" className="mt-1.5"
                value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
            </div>
            <div>
              <Label>Preview</Label>
              <div className="mt-1.5 flex items-center gap-2">
                <Badge className="text-white" style={{ backgroundColor: formData.color }}>
                  {formData.name || 'Tag Name'}
                </Badge>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-cyan-500 hover:bg-cyan-600">
              {editingTag ? 'Update' : 'Create'} Tag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TagsManager;
